'use client';

import { useCallback, type PointerEvent as ReactPointerEvent, type ReactNode, type Ref } from 'react';
import { cn } from '@/lib/utils';
import { MANUAL_PIP_WIDTH_PX, manualMinimapPx, type ManualStageLayout } from '@/lib/manual-layout';
import type { PointerMode } from '@/lib/manual-vector';
import { stageInsetsForHost, stageInsetsForReachedEdges } from './CopStageInsets';
import { EnclosingStageInsetsProvider, useStageInsets } from './useStageInsets';

/**
 * The map and the picture, at three sizes, in ONE DOM tree.
 *
 * That last part is the whole design of this file. The map node and the video
 * node keep the same position in the tree in every layout, and only their boxes
 * change — because moving a MapLibre map to a different parent unmounts and
 * recreates it, which means re-fetching the style, rebuilding every source and
 * losing the camera at the exact moment an operator is flying with it. So a
 * layout switch here is a class change and nothing more.
 *
 * The three layouts, and what each is for:
 *
 * | Layout | Map | Video | Why |
 * |---|---|---|---|
 * | `map` | the stage | corner inset | Default. Flying is a map task; the picture is confirmation. |
 * | `split` | half | half | Both at once, when the picture is part of the job. |
 * | `video` | 280 px locked minimap | the stage | The picture IS the job — an inspection, a search — and the control is reduced to what a control needs. |
 *
 * The minimap is an OPAQUE panel with its own border, not a transparent overlay
 * on the video. That was considered and rejected: map lines over moving video
 * lose legibility exactly when they are needed, and a control an operator has
 * to squint at is not one.
 *
 * This component owns none of the manual state. It reports that the operator
 * touched the map (`onMapInteraction`) and draws boxes; the lock, the layout
 * and the control surface all belong to the COP above it.
 */

export interface ManualStageProps {
  layout: ManualStageLayout;
  /**
   * The map column — `CopMapStage` and, inside it, the control surface. Passed
   * as children rather than as a prop because it is the stage's content in
   * every layout, including the one where it is 280 px square.
   */
  children: ReactNode;
  /** The picture. Null when there is nothing to show (no manual session). */
  video?: ReactNode;
  /**
   * Manual authority is held. Only then does the stage frame the minimap in the
   * authority colour, or report map interaction to the lock at all.
   */
  active?: boolean;
  /**
   * The operator touched the map. Fired for a press or a wheel anywhere in the
   * map box EXCEPT on the manual control surface's own controls, which are a
   * flight gesture and not a request to take the camera.
   *
   * Never fired in the VIDEO layout: the minimap is a control, not a map to
   * explore, and its lock is not the operator's to give up.
   */
  onMapInteraction?: () => void;
  /**
   * Measured by the COP, to decide whether MANUAL can be offered at all.
   *
   * Attached to an inert box sized to the UNCOVERED part of the map, not to the
   * map itself. The tactical shell's canvas is deliberately larger than the
   * visible map, so measuring the map box and subtracting would need every
   * consumer to know which floating chrome applies to it - and the VIDEO
   * layout's minimap, which is lifted clear of all of it, is exactly the case
   * that gets forgotten. One element, measured, no subtraction.
   */
  mapBoxRef?: Ref<HTMLDivElement>;
  /**
   * The uncovered STAGE, measured beside the map box and by the same rule (an
   * inert box under the same floating-chrome insets). In the regular-mode split
   * the map box is half the stage, but a session that opens in the MAP or VIDEO
   * layout flies from the whole of it, and the MANUAL key must be answered
   * about the box the session would use, not the one on screen now.
   */
  stageBoxRef?: Ref<HTMLDivElement>;
  /**
   * Which pointer is driving the stage. The ONLY thing it changes here is the
   * size of the control minimap: the contract's minimum control surface is
   * 280 px on a finger and 240 on a mouse, and this box is what has to satisfy
   * it in the VIDEO layout.
   */
  pointer?: PointerMode;
}

/**
 * What a press has to MISS to count as "the operator touched the map".
 *
 * Two exclusions, for two different reasons:
 *
 * - `[data-manual-surface]` is the flight control. Grabbing the halo is the
 *   gesture the camera lock exists to protect, so it must never be the thing
 *   that breaks it.
 * - Every other control in the map column — the strip board's rows, the map's
 *   own key column, the state card's buttons — sits inside this box but is not
 *   the map. Pressing one is an operator using the COP, not asking to look
 *   somewhere else, and freeing the camera for eight seconds because they
 *   selected a different aircraft from the strip board would be a surprise.
 *
 * What is left is the map canvas itself, which is exactly the target.
 */
const NOT_A_MAP_TOUCH =
  '[data-manual-surface], button, a[href], input, select, textarea, [role="button"], [role="tab"]';

export function ManualStage({
  layout,
  children,
  video = null,
  active = false,
  onMapInteraction,
  mapBoxRef,
  stageBoxRef,
  pointer = 'desktop',
}: ManualStageProps) {
  const minimap = layout === 'video';
  const minimapPx = manualMinimapPx(pointer);

  /**
   * The floating chrome the tactical shell puts over the stage.
   *
   * Only the minimap needs it, and only for its bottom edge: at 280 px wide it
   * reaches well past the corner clearance the bottom stack leaves, so at
   * `bottom-3` it would sit underneath the command bar - and the minimap in the
   * VIDEO layout is not decoration, it IS the flight control. Zero in the classic
   * shell and in any test without a provider, where this is exactly `bottom-3`.
   */
  const stageInsets = useStageInsets();

  /**
   * Nothing floats over the minimap, because the line below lifts it clear.
   *
   * The same rule has to hold for the box that is MEASURED and the surface that
   * is DRAWN, or the MANUAL key and the control surface disagree about whether
   * there is room to fly - which is the one thing `surfaceControllable` exists to
   * prevent.
   */
  /**
   * In SPLIT the stage's RIGHT edge belongs to the picture, not to the map.
   *
   * The two halves are flex siblings, so the map half stops where the picture
   * begins - and the side panel, which is anchored to the stage's right edge and
   * capped at half the viewport, covers the picture and none of the map. Reading
   * the stage's right inset here would take up to 560 px off a box nothing is
   * over, and the number is what decides whether MANUAL is offered at all.
   *
   * Only in SPLIT: in MAP the picture is a corner PIP that leaves the map the
   * whole stage, and in VIDEO the map is the minimap, which the line below lifts
   * clear of everything anyway.
   */
  const splitHalves = video !== null && layout === 'split';

  const mapBoxInsets = stageInsetsForReachedEdges(
    stageInsetsForHost(minimap, stageInsets),
    { right: !splitHalves },
  );
  // The stage box is never the lifted minimap, so it always carries the insets:
  // it stands for the map box a MAP-layout session would get, chrome and all.
  // It is the WHOLE stage, so it reaches every edge in every layout.
  const stageBoxInsets = stageInsetsForHost(false, stageInsets);

  const reportInteraction = useCallback(
    (event: ReactPointerEvent<HTMLDivElement> | React.WheelEvent<HTMLDivElement>) => {
      if (!active || minimap || !onMapInteraction) return;
      // Bubble phase, and the control surface stops propagation on its own
      // controls — but the STOP key does not, and neither would anything added
      // to the surface later. One explicit check covers all of them.
      if ((event.target as Element | null)?.closest?.(NOT_A_MAP_TOUCH)) return;
      onMapInteraction();
    },
    [active, minimap, onMapInteraction],
  );

  return (
    <div
      data-testid="manual-stage"
      data-layout={layout}
      className="relative flex min-h-0 min-w-0 flex-1 gap-3"
    >
      <div
        data-testid="manual-stage-map"
        onPointerDown={reportInteraction}
        onWheel={reportInteraction}
        className={cn(
          // Exactly one position utility, ever. `relative` and `absolute` are
          // the same CSS property, and Tailwind emits `.relative` after
          // `.absolute` — listing both would silently keep the minimap in flow.
          'flex min-h-0 min-w-0',
          minimap
            // Opaque, bordered, and over the picture rather than through it.
            // Square because `ringGeometry` measures the SHORTER side: a wide
            // box would spend pixels on a dimension the ring cannot use.
            ? 'absolute bottom-3 left-3 z-30 overflow-hidden rounded-lg border-2 bg-card shadow-xl'
            : 'relative flex-1',
          minimap && (active ? 'border-status-green/70' : 'border-subtle'),
        )}
        style={
          minimap
            ? {
                width: minimapPx,
                height: minimapPx,
                // `bottom-3` stays in the class list and is only overridden when
                // something is actually in the way, so the classic shell keeps
                // its rem-based offset and matches `left-3` under text zoom.
                ...(stageInsets.bottom > 0
                  ? { bottom: `calc(0.75rem + ${stageInsets.bottom}px)` }
                  : null),
                // Same for the left edge, where the navigation rail floats. This
                // box IS the flight control in the VIDEO layout, so a corner of
                // it under the rail is a corner of the control surface the
                // operator cannot see or reach.
                ...(stageInsets.left > 0
                  ? { left: `calc(0.75rem + ${stageInsets.left}px)` }
                  : null),
              }
            : undefined
        }
      >
        {/* The uncovered map, as an element rather than as arithmetic. Inert and
            invisible: it exists to be measured. */}
        <div
          ref={mapBoxRef}
          data-testid="manual-stage-map-box"
          aria-hidden
          className="pointer-events-none absolute"
          style={{
            top: mapBoxInsets.top,
            right: mapBoxInsets.right,
            bottom: mapBoxInsets.bottom,
            left: mapBoxInsets.left,
          }}
        />
        {/* The map's own chrome - the key column and the zones panel - is
            anchored to the edges of THIS box, not of the stage, so it has to
            step aside by the same insets the box above is measured with.
            Published rather than re-derived: the measured box and the drawn
            surfaces cannot be allowed to disagree. */}
        <EnclosingStageInsetsProvider insets={mapBoxInsets}>
          {children}
        </EnclosingStageInsetsProvider>
      </div>

      {video && (
        <div
          data-testid="manual-stage-video"
          className={cn(
            'min-h-0 min-w-0',
            layout === 'map'
              // Top right, clear of the map's own control column (44 px at
              // right-3) so the picture never sits on the navigation keys.
              ? 'absolute right-16 top-3 z-30 shadow-xl'
              : 'flex-1',
          )}
          style={
            layout === 'map'
              ? {
                  width: MANUAL_PIP_WIDTH_PX,
                  height: Math.round((MANUAL_PIP_WIDTH_PX * 9) / 16),
                  // Clear of the side panel as well as of the control column.
                  // This is the picture the operator is hand-flying off in the
                  // MAP layout; under a 320 px panel there is nothing left of
                  // it, and `right-16` alone cannot know that.
                  ...(stageInsets.right > 0
                    ? { right: `calc(4rem + ${stageInsets.right}px)` }
                    : null),
                  // And clear of the top stack, which in a manual session is
                  // holding the session strip itself (#505 slice 7) - so this
                  // collision is not an edge case, it is every MAP-layout
                  // session.
                  //
                  // Which of the two paints over the other is NOT the point, and
                  // an earlier version of this comment got it backwards: the
                  // stack is the stage column's FIRST child and this pane comes
                  // after it, and neither `ManualStage` nor `CopMapStage` sets a
                  // z-index, so at an equal `z-30` in the same stacking context
                  // DOM order wins and the PICTURE covers the strip. Either way
                  // one of them is unreadable, and the fix is that they do not
                  // overlap at all.
                  ...(stageInsets.top > 0
                    ? { top: `calc(0.75rem + ${stageInsets.top}px)` }
                    : null),
                }
              // In SPLIT and VIDEO the picture keeps the stage's FULL height,
              // level with the map beside it, even though the bottom stack
              // floats over its bottom edge exactly as it floats over the
              // map's. What has to clear the stack is not the pane but the
              // overlay's bottom band - the gimbal and zoom keys, the `GBL ✗` /
              // `ZM ✗` failure lines, and the latency badge that says how far
              // the picture is behind the aircraft, read while hand-flying off
              // it - and `ManualVideoStage` lifts that band by the same inset.
              // Shortening the pane with a margin instead (the first cut) left
              // the picture visibly shorter than the map next to it, which read
              // as the command bar cutting the screen (field review, 2026-09-03).
              : undefined
          }
        >
          {video}
        </div>
      )}

      {/* The uncovered stage, measured the same way as the map box above: what a
          session opening in the MAP or VIDEO layout would fly from while the
          regular-mode split holds the map box at half. Last in the tree so the
          map and the picture keep their places (the one-tree rule). Inert and
          invisible: it exists to be measured. */}
      <div
        ref={stageBoxRef}
        data-testid="manual-stage-box"
        aria-hidden
        className="pointer-events-none absolute"
        style={{
          top: stageBoxInsets.top,
          right: stageBoxInsets.right,
          bottom: stageBoxInsets.bottom,
          left: stageBoxInsets.left,
        }}
      />
    </div>
  );
}
